import snakeCase from 'lodash/snakeCase';
import { Locale, swapLocaleInPath } from '@tuic/i18n';
import { expandString } from '@tuic/web-compositions/utils/i18n';

type RequiredPlaceholders = {
  locale: Locale;
};

type PlaceholderValue = string | boolean | number | undefined | null;

export type UrlPlaceholders = RequiredPlaceholders & {
  [key: string]: PlaceholderValue;
};

type UrlPlaceholdersWithStringValues = RequiredPlaceholders & {
  [key: string]: string;
};

const UNDESIRED_URL_QUERY_PAIRS = [
  'x-tuic-webview=false',
  'inAppView=0',
  'webview=',
];

/**
 * Processes URLs by replacing placeholders, adding locale prefixes to relative URLs,
 * and removing undesired query parameters.
 *
 * @param {string | undefined} url - The URL to process
 * @param {UrlPlaceholders} placeholders - An object containing values to replace placeholders in the URL
 * @returns {string} The processed URL
 *
 * @example
 * // Returns '#' when URL is undefined
 * parseContentfulUrl(undefined, { locale: 'de' }); // => '#'
 *
 * // Keeps absolute URLs unchanged
 * parseContentfulUrl('https://www.wikipedia.org/', { locale: 'de' }); // => 'https://www.wikipedia.org/'
 *
 * // Adds locale prefix to relative URLs
 * parseContentfulUrl('/magazin', { locale: 'de' }); // => '/de/magazin'
 * parseContentfulUrl('magazin', { locale: 'de' }); // => '/de/magazin'
 * parseContentfulUrl('/magazin/beitrag', { locale: 'de' }); // => '/de/magazin/beitrag'
 *
 * // Replaces placeholders in URL paths
 * parseContentfulUrl('/product/:productId', { locale: 'de', productId: 123 }); // => '/de/product/123'
 * parseContentfulUrl('/product/{{ productId }}', { locale: 'de', productId: 123 }); // => '/de/product/123'
 * parseContentfulUrl('/product/{{ product_id }}', { locale: 'de', productId: 123 }); // => '/de/product/123'
 * parseContentfulUrl('https://example.com/product/{{ productId }}', { productId: 123 }); // => 'https://example.com/product/123'
 *
 * // Special handling for isWebview placeholder
 * parseContentfulUrl('/page?webview={{isWebview}}', { locale: 'de', isWebview: true }); // => '/de/page?webview=1'
 */
export const parseContentfulUrl = (
  url: string | undefined,
  placeholders: UrlPlaceholders,
): string => {
  if (typeof url === 'undefined') {
    return '#';
  }

  const parsedPlaceholders = castPlaceholdersToStrings(
    getExpandedUrlPlaceholders(placeholders),
  );

  let parsedUrl = url;

  parsedUrl = expandString(parsedUrl, parsedPlaceholders);
  parsedUrl = replaceLegacyUrlPlaceholders(parsedUrl, parsedPlaceholders);
  parsedUrl = addLocalePrefixToRelativeUrl(parsedUrl, placeholders.locale);

  return removeUndesiredUrlQueryParts(parsedUrl);
};

const addLocalePrefixToRelativeUrl = (url: string, locale: Locale) => {
  if (
    url.startsWith('http:') ||
    url.startsWith('https:') ||
    url.startsWith('mailto:') ||
    url.startsWith('tel:') ||
    url.startsWith('action:') ||
    url.startsWith('/api/')
  ) {
    return url;
  }

  return swapLocaleInPath(url, locale);
};

const castPlaceholdersToStrings = (
  placeholders: UrlPlaceholders,
): UrlPlaceholdersWithStringValues => {
  const casted: UrlPlaceholdersWithStringValues = {
    locale: placeholders.locale,
  };

  (Object.keys(placeholders) as (keyof typeof placeholders)[]).forEach(key => {
    casted[key] = castPlaceholderValue(key.toString(), placeholders[key]);
  });

  return casted;
};

const castPlaceholderValue = (key: string, value: PlaceholderValue): string => {
  if (typeof value === 'undefined' || value === null) {
    return '';
  }

  if (key === 'isWebview') {
    return value ? '1' : '';
  }

  return value.toString();
};

const replaceLegacyUrlPlaceholders = (
  url: string,
  placeholders: UrlPlaceholdersWithStringValues,
) => {
  // Skip replacement for action: protocol to avoid treating action:logout as :logout placeholder
  if (url.startsWith('action:')) {
    return url;
  }

  (Object.keys(placeholders) as (keyof UrlPlaceholders)[]).forEach(key => {
    const value = encodeURIComponent(placeholders[key]);

    url = url.split(`:${key}`).join(value);
  });

  return url;
};

const getExpandedUrlPlaceholders = (
  placeholders: UrlPlaceholders,
): UrlPlaceholders => {
  const expanded: UrlPlaceholders = {
    isWebviewNumeric: placeholders.isWebview ? '1' : '0',
    ...placeholders,
  };

  Object.keys(expanded).forEach(key => {
    const snakeCaseKey = snakeCase(key);
    expanded[snakeCaseKey] = expanded[key];
  });

  return expanded;
};

const removeUndesiredUrlQueryParts = (url: string): string => {
  const [urlWithoutQueryString, queryString] = url.split('?');

  if (!queryString) {
    return url;
  }

  const removePairs = UNDESIRED_URL_QUERY_PAIRS.map(pair => pair.toLowerCase());

  const parts = queryString.split('&').filter(nameWithValue => {
    return !removePairs.includes(nameWithValue.toLowerCase());
  });

  if (parts.length === 0) {
    return urlWithoutQueryString;
  }

  return `${urlWithoutQueryString}?${parts.join('&')}`;
};
